const formatPhpValue = (value, { integer = false } = {}) => {
  const numeric = Number(value || 0);
  const safeValue = Number.isFinite(numeric) ? numeric : 0;
  const roundedValue = integer ? Math.round(safeValue) : safeValue;
  const sign = roundedValue < 0 ? '-' : '';
  return `${sign}₱${Math.abs(roundedValue).toLocaleString('en-PH')}`;
};

export const formatCurrency = (value) => formatPhpValue(value);

export const formatNumber = (value) => Number(value || 0).toLocaleString('en-PH');

const toIntegerValue = (value) => {
  const numeric = Number(value || 0);
  return Number.isFinite(numeric) ? Math.round(numeric) : 0;
};

export const formatIntegerNumber = (value) => toIntegerValue(value).toLocaleString('en-PH');

export const formatIntegerCurrency = (value) => formatPhpValue(value, { integer: true });

export const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-PH', { timeZone: 'Asia/Manila' });
};

export const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-PH', { timeZone: 'Asia/Manila' });
};

export const getUserDisplayName = (user) => user?.name || user?.nickname || user?.phoneNumber || user?.phone || 'Unknown';

export const getUserPhone = (user) => user?.phoneNumber || user?.phone || '-';

export const isSameMonth = (value, reference = new Date()) => {
  const date = new Date(value);
  return (
    !Number.isNaN(date.getTime()) &&
    date.getFullYear() === reference.getFullYear() &&
    date.getMonth() === reference.getMonth()
  );
};

export const getDaysElapsedInMonth = (reference = new Date()) => {
  const today = new Date(reference);
  return today.getDate();
};

export const calculateComplianceRate = ({ completedCheckins = 0, unitCount = 0, daysElapsed = getDaysElapsedInMonth() }) => {
  const requiredCheckins = unitCount * daysElapsed;
  if (!requiredCheckins) return 0;
  return Math.min(100, Math.round((completedCheckins / requiredCheckins) * 100));
};

export const getInspectionDisplayOverallStatus = (record) => {
  if (!record) return null;
  if (Object.prototype.hasOwnProperty.call(record, 'displayOverallStatus')) {
    return record.displayOverallStatus;
  }
  return 'normal';
};

export const getInspectionDisplaySummary = (t, record = {}) => {
  const items = Array.isArray(record.items) ? record.items : [];
  const total = Number(record.itemCount ?? items.length ?? 0);
  const pending = items.filter((item) => item?.status === 'pending').length;
  const completed = Math.max(0, total - pending);

  return t('inspectionDisplay.summary', {
    completed,
    total: Math.max(0, total),
    defaultValue: `Completed ${completed}/${Math.max(0, total)} inspection items. No abnormalities displayed.`
  });
};

export const getInspectionDisplayItemStatus = (item) => (
  item?.status === 'pending' ? 'pending' : 'completed'
);

export const isCheckinAbnormal = (record) => (
  getInspectionDisplayOverallStatus(record) === 'abnormal'
);

export const extractPhotoUrls = (record) => {
  const list = [];
  if (record?.photoUrl) {
    list.push(record.photoUrl);
  }
  if (Array.isArray(record?.photoUrls)) {
    record.photoUrls.forEach((url) => {
      if (url) list.push(url);
    });
  }
  if (Array.isArray(record?.items)) {
    record.items.forEach((item) => {
      if (Array.isArray(item?.photoUrls)) {
        item.photoUrls.forEach((url) => {
          if (url) list.push(url);
        });
      }
    });
  }
  return [...new Set(list)];
};

export const buildWeeklyTrend = (records = []) => {
  const days = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  for (let index = 6; index >= 0; index -= 1) {
    const date = new Date(today);
    date.setDate(today.getDate() - index);
    const nextDate = new Date(date);
    nextDate.setDate(date.getDate() + 1);

    const count = records.filter((record) => {
      const createdAt = new Date(record?.createdAt);
      return createdAt >= date && createdAt < nextDate;
    }).length;

    days.push({
      key: date.toISOString().slice(0, 10),
      label: `${date.getMonth() + 1}/${date.getDate()}`,
      count
    });
  }

  return days;
};
